import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map routes to tab names
const getTabName = (pathname) => {
  const routeMap = {
    '/': 'Home',
    '/schemes': 'Schemes',
    '/news': 'News',
    '/profile': 'Profile',
    '/subscription': 'Subscription',
    '/saved-schemes': 'Saved Schemes',
    '/apply-help': 'Help Me Apply',
    '/about': 'About',
    '/careers': 'Careers',
    '/future': 'Future',
    '/login': 'Login',
    '/signup': 'Signup'
  };
  
  // Exclude admin-related routes from tracking
  if (pathname.startsWith('/admin')) {
    return null; // Don't track admin pages
  }
  
  // Check for dynamic routes like /schemes/:id
  if (pathname.startsWith('/schemes/')) {
    return 'Scheme Details';
  }
  
  return routeMap[pathname] || 'Other';
};

// Generate anonymous session ID for non-logged in users
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('anonymous_session_id');
  if (!sessionId) {
    sessionId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('anonymous_session_id', sessionId);
  }
  return sessionId;
};

const TabTimeTracker = () => {
  const location = useLocation();
  const { user, token } = useAuth();
  
  // Refs to track time
  const currentTabRef = useRef(null);
  const tabStartTimeRef = useRef(null);
  const isActiveRef = useRef(true);
  const activeTimeRef = useRef(0);
  const lastActiveTimeRef = useRef(Date.now());
  
  // Send tab time data to backend
  const sendTabTime = useCallback(async (tabName, timeSpent) => {
    if (timeSpent < 1) return; // Don't send if less than 1 second
    
    try {
      const payload = {
        tab_name: tabName,
        time_spent: Math.round(timeSpent),
        timestamp: new Date().toISOString(),
        user_id: user?.id || null,
        session_id: user?.id ? null : getSessionId()
      };
      
      // Use sendBeacon for reliability on page unload
      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(`${API_URL}/api/analytics/tab-time`, blob);
      } else {
        // Fallback to fetch
        await fetch(`${API_URL}/api/analytics/tab-time`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify(payload)
        });
      }
    } catch (error) {
      console.error('Failed to send tab time:', error);
    }
  }, [user, token]);
  
  // Calculate and send current tab time
  const flushCurrentTabTime = useCallback(() => {
    if (currentTabRef.current && tabStartTimeRef.current) {
      // Add remaining active time
      if (isActiveRef.current) {
        activeTimeRef.current += (Date.now() - lastActiveTimeRef.current) / 1000;
      }
      
      const timeSpent = activeTimeRef.current;
      if (timeSpent >= 1) {
        sendTabTime(currentTabRef.current, timeSpent);
      }
    }
  }, [sendTabTime]);
  
  // Handle visibility change (tab focus/blur)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab became hidden - pause tracking
        if (isActiveRef.current) {
          activeTimeRef.current += (Date.now() - lastActiveTimeRef.current) / 1000;
          isActiveRef.current = false;
        }
      } else {
        // Tab became visible - resume tracking
        isActiveRef.current = true;
        lastActiveTimeRef.current = Date.now();
      }
    };
    
    const handleFocus = () => {
      isActiveRef.current = true;
      lastActiveTimeRef.current = Date.now();
    };
    
    const handleBlur = () => {
      if (isActiveRef.current) {
        activeTimeRef.current += (Date.now() - lastActiveTimeRef.current) / 1000;
        isActiveRef.current = false;
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);
  
  // Handle route changes
  useEffect(() => {
    const newTabName = getTabName(location.pathname);
    
    // If switching to a different tab, send the previous tab's time
    if (currentTabRef.current && currentTabRef.current !== newTabName) {
      flushCurrentTabTime();
    }
    
    // Skip tracking for admin pages (when newTabName is null)
    if (!newTabName) {
      currentTabRef.current = null;
      return;
    }
    
    // Start tracking new tab
    currentTabRef.current = newTabName;
    tabStartTimeRef.current = Date.now();
    activeTimeRef.current = 0;
    lastActiveTimeRef.current = Date.now();
    isActiveRef.current = !document.hidden;
    
  }, [location.pathname, flushCurrentTabTime]);
  
  // Send data on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      flushCurrentTabTime();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also flush when component unmounts
      flushCurrentTabTime();
    };
  }, [flushCurrentTabTime]);
  
  // Periodic sync every 30 seconds to prevent data loss
  useEffect(() => {
    const interval = setInterval(() => {
      if (currentTabRef.current && activeTimeRef.current >= 5) {
        // Add current active time
        if (isActiveRef.current) {
          activeTimeRef.current += (Date.now() - lastActiveTimeRef.current) / 1000;
          lastActiveTimeRef.current = Date.now();
        }
        
        // Send accumulated time
        sendTabTime(currentTabRef.current, activeTimeRef.current);
        
        // Reset counter but keep tracking
        activeTimeRef.current = 0;
      }
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, [sendTabTime]);
  
  return null; // This component doesn't render anything
};

export default TabTimeTracker;
